import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Plus, Calendar, Users, MapPin, ArrowRight, Zap, Trophy, Sparkles, Wallet as WalletIcon, TrendingUp } from "lucide-react";
import { format, isToday, isTomorrow } from "date-fns";
import GuidedTour from "@/components/GuidedTour";

interface KrewWithMembers {
  id: string; title: string; event_date: string; event_time: string; status: string;
  city: string; area: string | null; budget_per_person: number; vibe: string[] | null;
  invite_code: string; creator_id: string; member_count?: number;
}

const statusColors: Record<string, string> = {
  planning: "bg-secondary/20 text-secondary",
  committed: "bg-primary/20 text-primary",
  venue_voting: "bg-purple-500/20 text-purple-400",
  booked: "bg-green-500/20 text-green-400",
  active: "bg-primary/20 text-primary",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/20 text-destructive",
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Night owl mode";
};

const Dashboard = () => {
  const { user, profile } = useAuth();
  const [krews, setKrews] = useState<KrewWithMembers[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchKrews = async () => {
      const { data: memberKrewIds } = await supabase
        .from("krew_members")
        .select("krew_id")
        .eq("user_id", user.id);

      const krewIds = memberKrewIds?.map(m => m.krew_id) || [];

      const { data: createdKrews } = await supabase
        .from("krews")
        .select("id")
        .eq("creator_id", user.id);

      const allIds = [...new Set([...krewIds, ...(createdKrews?.map(k => k.id) || [])])];

      if (allIds.length === 0) { setLoading(false); return; }

      const { data } = await supabase
        .from("krews")
        .select("*")
        .in("id", allIds)
        .order("event_date", { ascending: true });

      if (data) {
        const krewsWithCounts = await Promise.all(
          data.map(async (krew) => {
            const { count } = await supabase.from("krew_members").select("*", { count: "exact", head: true }).eq("krew_id", krew.id);
            return { ...krew, member_count: count || 0 } as KrewWithMembers;
          })
        );
        setKrews(krewsWithCounts);
      }
      setLoading(false);
    };
    fetchKrews();
  }, [user]);

  const activeKrews = krews.filter((k) => !["completed", "cancelled"].includes(k.status));
  const pastKrews = krews.filter((k) => ["completed", "cancelled"].includes(k.status));
  const tonightKrew = activeKrews.find(k => isToday(new Date(k.event_date)));
  const tomorrowKrew = !tonightKrew ? activeKrews.find(k => isTomorrow(new Date(k.event_date))) : null;
  const urgentKrew = tonightKrew || tomorrowKrew;

  return (
    <div className="space-y-8">
      <GuidedTour />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {getGreeting()}, <span className="text-gradient-neon">{profile?.display_name || "there"}</span> 👋
          </h1>
          <p className="mt-1 text-muted-foreground font-body">
            {tonightKrew ? "You've got a night out tonight! 🔥" :
             activeKrews.length > 0 ? `${activeKrews.length} active Krew${activeKrews.length > 1 ? "s" : ""} lined up` :
             "Ready for your next legendary night?"}
          </p>
        </div>
        <Link to="/create-krew" data-tour="create-krew" className="group flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02]">
          <Plus className="h-4 w-4" /> New Krew
        </Link>
      </div>

      {/* Tonight/Tomorrow Banner */}
      {urgentKrew && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Link to={`/krew/${urgentKrew.id}`} className="block rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 to-secondary/10 p-6 transition-all hover:border-primary/50">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-sm font-bold text-primary">{tonightKrew ? "TONIGHT" : "TOMORROW"}</span>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-1">{urgentKrew.title}</h2>
            <p className="text-sm text-muted-foreground">
              {format(new Date(urgentKrew.event_date), "EEEE, MMM d")} at {urgentKrew.event_time?.slice(0, 5)} · {urgentKrew.area || urgentKrew.city}
            </p>
          </Link>
        </motion.div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Link to="/venues" data-tour="browse-venues" className="rounded-xl border border-border bg-card p-4 text-center transition-all hover:border-primary/30 hover:bg-card/80">
          <MapPin className="mx-auto mb-2 h-5 w-5 text-primary" />
          <p className="text-sm font-medium text-foreground">Browse Venues</p>
        </Link>
        <Link to="/wallet" data-tour="wallet" className="rounded-xl border border-border bg-card p-4 text-center transition-all hover:border-primary/30 hover:bg-card/80">
          <WalletIcon className="mx-auto mb-2 h-5 w-5 text-secondary" />
          <p className="text-sm font-medium text-foreground">View Wallet</p>
        </Link>
        <Link to="/create-krew" className="rounded-xl border border-border bg-card p-4 text-center transition-all hover:border-primary/30 hover:bg-card/80">
          <Plus className="mx-auto mb-2 h-5 w-5 text-primary" />
          <p className="text-sm font-medium text-foreground">Create Krew</p>
        </Link>
        <Link to="/profile" className="rounded-xl border border-border bg-card p-4 text-center transition-all hover:border-primary/30 hover:bg-card/80">
          <Trophy className="mx-auto mb-2 h-5 w-5 text-secondary" />
          <p className="text-sm font-medium text-foreground">My Profile</p>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-tour="stats">
        {[
          { icon: Zap, value: profile?.total_krews_joined || 0, label: "Krews Joined", color: "text-primary", bg: "bg-primary/10" },
          { icon: Trophy, value: `${profile?.reliability_score || 100}%`, label: "Reliability", color: "text-secondary", bg: "bg-secondary/10" },
          { icon: Users, value: profile?.total_krews_attended || 0, label: "Nights Out", color: "text-green-400", bg: "bg-green-500/10" },
        ].map((stat, i) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${stat.bg}`}>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Active Krews */}
      <section>
        <h2 className="mb-4 text-xl font-bold text-foreground">Active Krews</h2>
        {loading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[1, 2].map((i) => <div key={i} className="h-48 animate-pulse rounded-2xl bg-card" />)}
          </div>
        ) : activeKrews.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-dashed border-border p-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10"><Plus className="h-8 w-8 text-primary" /></div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">No active Krews</h3>
            <p className="mb-6 text-sm text-muted-foreground">Create your first Krew and invite your squad!</p>
            <Link to="/create-krew" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground glow-neon">
              Create a Krew <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {activeKrews.map((krew, i) => (
              <motion.div key={krew.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Link to={`/krew/${krew.id}`} className="group block rounded-2xl border border-border bg-card p-6 transition-all hover:border-primary/30 hover:shadow-lg">
                  <div className="mb-3 flex items-center justify-between">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusColors[krew.status] || statusColors.planning}`}>{krew.status.replace("_", " ")}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-foreground">{krew.title}</h3>
                  <div className="space-y-1.5 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><Calendar className="h-4 w-4" />{format(new Date(krew.event_date), "EEE, MMM d")} · {krew.event_time?.slice(0, 5)}</div>
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4" />{krew.area || krew.city}</div>
                    <div className="flex items-center gap-2"><Users className="h-4 w-4" />{krew.member_count} member{krew.member_count !== 1 ? "s" : ""}</div>
                  </div>
                  {krew.vibe?.length ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {krew.vibe.slice(0, 3).map((v) => <span key={v} className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">{v}</span>)}
                    </div>
                  ) : null}
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Past Krews */}
      {pastKrews.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Past Nights</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {pastKrews.map((krew) => (
              <Link key={krew.id} to={`/krew/${krew.id}`} className="rounded-xl border border-border bg-card/50 p-4 transition-all hover:bg-card">
                <h3 className="font-semibold text-foreground">{krew.title}</h3>
                <p className="text-sm text-muted-foreground">{format(new Date(krew.event_date), "MMM d, yyyy")}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
